import { useEffect, useMemo, useRef } from "react";
import { create } from "zustand";
import { sound } from "../audio/sound";
import { PRIVACY_MATERIAL_VERSION, privacyLinkVisible } from "../legal/privacy";
import { subscribeToGame } from "../net/onlineClient";
import {
  acknowledgePrivacy,
  subscribeToVoiceRoom,
  voiceErrorCode,
  voiceHostMute,
  voiceJoin,
  voiceLeave,
  voiceTable,
} from "../net/voiceApi";
import {
  autoJoinEnabled,
  getVoiceLevel,
  isAgeGated,
  loadPrivacyAck,
  mutedCalloutRetired,
  recordUnmutedMatch,
  voiceReadyBand,
} from "../voice/consent";
import {
  isMutedIn,
  loadMutes,
  mute,
  unmute,
  type MuteList,
} from "../voice/mutes";
import {
  derivedRoomArrival,
  roomArrival,
  roomArrivalText,
  roomPulls,
  type RoomArrival,
  type RoomLobby,
} from "../voice/rooms";
import { NO_HOST_MUTE, type HostMute, type VoiceMember } from "../voice/rules";
import { VoiceClient } from "../voice/voiceClient";
import {
  ensureVoiceContext,
  localMicLevel,
  onVoiceAudioChange,
  releaseLocalMicMeter,
  remoteLevel,
  setVoiceOutputLevel,
  silenceRemote,
  unlockVoiceAudio,
  voiceAudioNeedsUnlock,
} from "../voice/voiceAudio";
import { notify } from "./notifications";

/**
 * Voice chat for the match this client is in.
 *
 * A module-level store rather than component state, because the screens it
 * serves do not live long enough: OnlinePlayScreen remounts on every table hop,
 * and a match moves between the lobby, the table and the leaderboard all
 * evening. Voice has to carry on across all of that without a reconnect, so the
 * screens only RETAIN it (useVoiceMatch) and it is released for real a few
 * seconds after the last of them goes - long enough to survive a route change,
 * short enough that leaving the match leaves the room.
 *
 * `idle` covers "could join but has not": a 13-17 player, or anybody with
 * "Join voice automatically" off, sees "Join voice" on the mic button.
 * `table-off` is the host having turned voice off for the match; it lifts by
 * itself when they turn it back on.
 *
 * ONE roster for the match, but not one room: in a party split across tables
 * each player hears the table they are being SHOWN (rooms.ts). The screens say
 * which room that is (useVoiceMatch's last argument), and the store reads the
 * lobby's public document itself to work out who else is in it.
 */
export type VoiceStatus =
  | "idle"
  | "needs-consent"
  | "under13"
  | "joining"
  | "listening"
  | "unavailable"
  | "error"
  | "table-off";

/** A screen release waits this long before leaving, so a route change between two online screens does not drop voice. */
const RELEASE_DELAY_MS = 5_000;
/** A hidden tab leaves the room after this long; a page being unloaded leaves at once. */
const HIDDEN_LEAVE_MS = 5 * 60_000;
/** Speaking: RMS over this is a voice, and the dot is held this long after it stops. */
const SPEAKING_RMS = 0.02;
const SPEAKING_HOLD_MS = 350;
const SPEAKING_SAMPLE_MS = 100;
/** A session that breaks more often than this is given up on rather than rejoined forever. */
const MAX_RESETS = 3;
const RESET_WINDOW_MS = 2 * 60_000;

interface VoiceStoreState {
  status: VoiceStatus;
  /** The lobby game id - the party parent in a party - which is the voice roster's id and every route's. */
  roomId: string | null;
  myPlayerId: string | null;
  /** The room the screen is showing - the lobby id, or a party table's - which is who gets pulled. */
  screenRoomId: string | null;
  /** The lobby's public document, cut down to what a room is derived from. Null until it arrives. */
  lobby: RoomLobby | null;
  hostId: string | null;
  /** `voice/{roomId}`.members: who is in voice, as the server last said. */
  members: Record<string, VoiceMember>;
  hostMute: HostMute;
  tableOff: boolean;
  micOn: boolean;
  /** This client has a live published track this match. */
  published: boolean;
  /** Between the first unmute and the publish landing: the mic permission prompt lives here. */
  micBusy: boolean;
  /** The browser refused the microphone. */
  micBlocked: boolean;
  /** Something is playing that nobody can hear until a tap starts audio. */
  needsAudioUnlock: boolean;
  speaking: Record<string, boolean>;
  /** ✕ on the callout, for this match. */
  calloutDismissed: boolean;
  /** The "You're muted" callout has done its job (see consent.ts). */
  calloutRetired: boolean;
  /** The voice consent sheet is open. */
  sheetOpen: boolean;
  /** Players this device has muted, resolved against the roster's nicknames. */
  mutedIds: Record<string, true>;

  retain: (lobbyId: string, myPlayerId: string) => void;
  release: (lobbyId: string) => void;
  /** The screen moved to another room: pulls follow it, and arriving is announced. */
  setScreenRoom: (lobbyId: string, screenRoomId: string) => void;
  /** The match's players, for the nickname half of a personal mute. */
  setRoster: (players: { id: string; nickname: string }[]) => void;
  /** `interactive` when a tap asked for it: only then may a refusal open the consent sheet over the table. */
  join: (interactive?: boolean) => Promise<void>;
  leave: () => void;
  toggleMic: () => void;
  mutePlayer: (playerId: string, nickname: string) => void;
  unmutePlayer: (playerId: string, nickname: string) => void;
  unlockAudio: () => void;
  refreshOutput: () => void;
  dismissCallout: () => void;
  openSheet: () => void;
  closeSheet: () => void;
  /** The sheet recorded an acknowledgement with an age band; join, because the player just asked to. */
  consented: () => void;
  /** The sheet's age answer was under 13. */
  markUnder13: () => void;
  // The host's controls. Each resolves false on a refusal; the roster is what
  // redraws the controls either way, so a failed one leaves them as they were.
  hostMutePlayer: (playerId: string, muted: boolean) => Promise<boolean>;
  hostMuteAll: (all: boolean) => Promise<boolean>;
  setTableVoice: (off: boolean) => Promise<boolean>;
}

// ---- state that is not rendered --------------------------------------------

let refs = 0;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let client: VoiceClient | null = null;
let unsubscribeRoom: (() => void) | null = null;
let unsubscribeLobby: (() => void) | null = null;
/** A room the screen moved to while listening, whose notice waits for a lobby document that lists it. */
let pendingArrival: string | null = null;
/**
 * The last room notice shown, so the two ways of noticing one change in a party
 * (the screen moving rooms, and the lobby document moving the derived room) say
 * it once. Compared by text within a short window rather than by room: the two
 * land in either order, a few hundred ms apart at most.
 */
let lastNotice: { text: string; at: number } | null = null;
const NOTICE_DEDUPE_MS = 4_000;
/** An observer arrived while their first publish was in flight; it lands with the mic off. */
let micOffAfterPublish = false;
/** Bumped whenever the lobby changes; a roster snapshot for an older one is dropped. */
let lobbyGen = 0;
/** Bumped whenever the session ends; a join or publish finishing for an older one is dropped. */
let sessionGen = 0;
let muteList: MuteList = { ids: [], nicknames: [] };
const nicknames = new Map<string, string>();
let speakingTimer: ReturnType<typeof setInterval> | null = null;
const lastLoudAt = new Map<string, number>();
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
/** Voice was left because the page went away, and comes back with it if auto-join is on. */
let leftForHide = false;
/** The server refused for want of an acknowledgement and the local one has been re-posted once. */
let ackReposted = false;
let resets: number[] = [];
let pageListenersInstalled = false;

const IDLE_SESSION = {
  micOn: false,
  published: false,
  micBusy: false,
  needsAudioUnlock: false,
  speaking: {},
} satisfies Partial<VoiceStoreState>;

function readyBand() {
  return voiceReadyBand(loadPrivacyAck(), PRIVACY_MATERIAL_VERSION);
}

/**
 * Whether arriving should join without a tap. 18+ only, whatever the setting
 * says for a 13-17 player: auto-joining a room of strangers is the default the
 * UK Children's Code asks services used by children not to have.
 */
function shouldAutoJoin(): boolean {
  const band = readyBand();
  return band === "18+" && autoJoinEnabled(band);
}

/** Whoever holds the host seat, seated or waiting - the server's rule for who may use the host routes. */
function hostOf(state: {
  players?: { id: string; isHost: boolean }[];
  pendingPlayers?: { id: string; isHost: boolean }[];
  observers?: { id: string; isHost: boolean }[];
}): string | null {
  // The host may be watching rather than seated.
  return (
    [
      ...(state.players ?? []),
      ...(state.pendingPlayers ?? []),
      ...(state.observers ?? []),
    ].find((p) => p.isHost)?.id ?? null
  );
}

/**
 * Refused, absent or held by another app: the player's to fix, so the button
 * says so. Anything else was the session, which reconnects itself.
 */
function micRefused(err: unknown): boolean {
  const name = err instanceof DOMException ? err.name : "";
  return [
    "NotAllowedError",
    "SecurityError",
    "NotFoundError",
    "NotReadableError",
    "OverconstrainedError",
    "NotSupportedError",
  ].includes(name);
}

async function hostCall(call: () => Promise<void>): Promise<boolean> {
  try {
    await call();
    return true;
  } catch (err) {
    if (import.meta.env.DEV) console.warn("[voice] host control refused:", err);
    return false;
  }
}

export const useVoiceStore = create<VoiceStoreState>((set, get) => {
  const isMutedFor = (roomId: string) => (playerId: string) =>
    isMutedIn(muteList, playerId, nicknames.get(playerId), roomId);

  function recomputeMuted(): void {
    const { roomId, members } = get();
    if (!roomId) return;
    const check = isMutedFor(roomId);
    const mutedIds: Record<string, true> = {};
    for (const id of new Set([...Object.keys(members), ...nicknames.keys()]))
      if (check(id)) mutedIds[id] = true;
    const prev = get().mutedIds;
    const same =
      Object.keys(prev).length === Object.keys(mutedIds).length &&
      Object.keys(mutedIds).every((id) => prev[id]);
    if (!same) set({ mutedIds });
  }

  function reconcile(): void {
    if (client && get().status === "listening") client.sync();
  }

  /** Hangs up this client's side; the server is told separately (or not at all, when a new join replaces it). */
  function closeSession(): void {
    sessionGen++;
    micOffAfterPublish = false;
    pendingArrival = null;
    client?.close();
    client = null;
    if (speakingTimer !== null) clearInterval(speakingTimer);
    speakingTimer = null;
    lastLoudAt.clear();
    releaseLocalMicMeter();
  }

  function startSpeaking(): void {
    if (speakingTimer !== null) clearInterval(speakingTimer);
    speakingTimer = setInterval(() => {
      if (!client) return;
      const now = Date.now();
      const { members, speaking, myPlayerId, micOn } = get();
      const next: Record<string, boolean> = {};
      // The player's own mic, so their name lights when they talk. `micOn` is
      // what the store treats as sending (muting, a host mute and push-to-talk
      // all clear it), and the track is only there while published.
      const mic = client.micTrack;
      if (myPlayerId && micOn && mic) {
        if (localMicLevel(mic) > SPEAKING_RMS) lastLoudAt.set(myPlayerId, now);
        if (now - (lastLoudAt.get(myPlayerId) ?? 0) < SPEAKING_HOLD_MS)
          next[myPlayerId] = true;
      } else {
        // Nothing held over from before a mute: the light goes out with the mic.
        lastLoudAt.delete(myPlayerId ?? "");
        releaseLocalMicMeter();
      }
      for (const id of client.pulledPlayerIds) {
        // Only somebody whose mic is on can be speaking; a level off a muted
        // track would be comfort noise.
        if (
          members[id] &&
          !members[id].selfMuted &&
          remoteLevel(id) > SPEAKING_RMS
        )
          lastLoudAt.set(id, now);
        if (now - (lastLoudAt.get(id) ?? 0) < SPEAKING_HOLD_MS) next[id] = true;
      }
      const changed =
        Object.keys(next).length !== Object.keys(speaking).length ||
        Object.keys(next).some((id) => !speaking[id]);
      if (changed) set({ speaking: next });
    }, SPEAKING_SAMPLE_MS);
  }

  /**
   * The session went somewhere this side and Cloudflare may not agree about
   * (see voiceClient.ts). Join again, listening with the mic off - the same
   * place every join starts, and never a mic that comes back on by itself.
   */
  function reconnect(): void {
    const now = Date.now();
    resets = resets.filter((t) => now - t < RESET_WINDOW_MS);
    closeSession();
    if (resets.length >= MAX_RESETS) {
      const { roomId } = get();
      if (roomId) void voiceLeave(roomId).catch(() => {});
      set({ ...IDLE_SESSION, status: "error" });
      return;
    }
    resets.push(now);
    set({ ...IDLE_SESSION, status: "idle" });
    const gen = sessionGen;
    setTimeout(() => {
      if (gen === sessionGen && get().status === "idle") void get().join();
    }, 1000 * resets.length);
  }

  /**
   * A fresh Cloudflare session, still listening, mic off - for when the old
   * one is done rather than broken: the host force-closed this player's track,
   * or the last pull was closed. Deliberately NOT a reset: those count towards
   * giving up, and neither of these is anything going wrong.
   */
  function renewSession(): void {
    if (get().status !== "listening") return;
    closeSession();
    set({ ...IDLE_SESSION, status: "idle" });
    void get().join();
  }

  /** Leaves because the page is going away or has been hidden too long. */
  function leaveForHide(keepalive: boolean): void {
    const { roomId, status } = get();
    if (!roomId || (status !== "listening" && status !== "joining")) return;
    leftForHide = true;
    closeSession();
    void voiceLeave(roomId, { keepalive }).catch(() => {});
    set({ ...IDLE_SESSION, status: "idle" });
  }

  function cameBack(): void {
    if (hiddenTimer !== null) clearTimeout(hiddenTimer);
    hiddenTimer = null;
    const { status, roomId } = get();
    if (leftForHide) {
      leftForHide = false;
      if (roomId && status === "idle" && shouldAutoJoin()) void get().join();
      return;
    }
    // iOS interrupts a context in the background; asking it to run again is
    // free, and if it refuses, the callout asks for the tap.
    if (status === "listening") {
      ensureVoiceContext();
      set({ needsAudioUnlock: voiceAudioNeedsUnlock() });
    }
  }

  function installPageListeners(): void {
    if (pageListenersInstalled) return;
    pageListenersInstalled = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        cameBack();
        return;
      }
      if (hiddenTimer !== null) clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(() => {
        hiddenTimer = null;
        leaveForHide(false);
      }, HIDDEN_LEAVE_MS);
    });
    // pagehide rather than unload: it is the one that fires on mobile and for
    // the back-forward cache, and `pageshow` brings a cached page back.
    window.addEventListener("pagehide", () => leaveForHide(true));
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) cameBack();
    });
    onVoiceAudioChange(() => {
      const listening = get().status === "listening";
      set({ needsAudioUnlock: listening && voiceAudioNeedsUnlock() });
    });
  }

  /** Mic off from the store's side, the way a tap turns it off: locally and on the roster. */
  function micOff(): void {
    const c = client;
    if (!c) return;
    if (get().micBusy) {
      micOffAfterPublish = true;
      return;
    }
    if (!get().micOn) return;
    c.mute();
    set({ micOn: false });
    c.sendMicState(true).catch(() => {});
  }

  /**
   * Announces the room the screen just moved to, once the lobby document can
   * say what it is (rooms.ts' roomArrival). An observer arrives with the mic
   * off, whatever it was at their own table: a crowd that walked in talking
   * would drown the table still playing. Seated players keep theirs.
   */
  function arrive(): void {
    if (!pendingArrival) return;
    const { status, lobby, roomId, myPlayerId, screenRoomId } = get();
    if (
      status !== "listening" ||
      pendingArrival !== screenRoomId ||
      !roomId ||
      !myPlayerId
    ) {
      pendingArrival = null;
      return;
    }
    const arrival = roomArrival(lobby, roomId, myPlayerId, pendingArrival);
    if (!arrival) return;
    pendingArrival = null;
    announce(arrival);
    if (arrival.kind === "observer" || arrival.kind === "all-tables") micOff();
  }

  function announce(arrival: RoomArrival): void {
    const text = roomArrivalText(arrival);
    const now = Date.now();
    if (
      lastNotice &&
      lastNotice.text === text &&
      now - lastNotice.at < NOTICE_DEDUPE_MS
    )
      return;
    lastNotice = { text, at: now };
    notify({ title: text, durationMs: 2200, scale: 0.72 });
  }

  /**
   * A Teams round dealt at ONE table moves this player's derived room without
   * the screen's room changing (it is the lobby id throughout), so `arrive`
   * never fires. Announce it from the lobby document instead. Notice only: the
   * mic is left exactly as it was.
   */
  function announceDerivedChange(
    prev: RoomLobby | null,
    next: RoomLobby,
  ): void {
    const { status, myPlayerId } = get();
    if (status !== "listening" || !myPlayerId) return;
    const arrival = derivedRoomArrival(prev, next, myPlayerId);
    if (arrival) announce(arrival);
  }

  /**
   * The host turned voice off for the table: the server has already removed
   * every member, so this side only hangs up - a leave would be a request about
   * a membership that no longer exists. When they turn it back on, voice comes
   * back the way arriving brings it: by itself only where auto-join applies.
   */
  function applyTableOff(off: boolean): void {
    const { status } = get();
    if (off) {
      if (
        status === "listening" ||
        status === "joining" ||
        status === "idle" ||
        status === "error"
      ) {
        closeSession();
        set({ ...IDLE_SESSION, status: "table-off" });
      }
      return;
    }
    if (status !== "table-off") return;
    set({ status: "idle" });
    if (shouldAutoJoin()) void get().join();
  }

  /**
   * A host mute force-closes the muted player's published track on Cloudflare
   * and marks them unpublished, but this client still holds the transceiver,
   * which would make the next unmute think it only has to enable a dead track.
   * Dropping the transceiver is not enough either: spike 1.2 (test 4) found a
   * re-publish on the SAME session fails after a force-close and only a new
   * session carries audio again. So the session is renewed here - listening
   * goes on after a moment's gap - and the mic is off, which is where it stays
   * when the host lifts the mute: a player's own mic-off is never the host's
   * to turn back on.
   *
   * Only while host-muted: a snapshot written just before this client's own
   * publish landed also says `published: false`, and must not undo it.
   */
  function followHostMute(): void {
    const { myPlayerId, members, status, micOn } = get();
    const me = myPlayerId ? members[myPlayerId] : undefined;
    const c = client;
    if (status !== "listening" || !c || !me?.hostMuted) return;
    if (c.published && !me.published) {
      renewSession();
    } else if (micOn) {
      // Muted but not yet force-closed (the server closes outside its write):
      // silent now rather than when the close lands.
      c.setMicEnabled(false);
      set({ micOn: false });
    }
  }

  /** Everything off, for a lobby this client no longer belongs to. */
  function teardown(): void {
    const { roomId, status } = get();
    if (releaseTimer !== null) clearTimeout(releaseTimer);
    releaseTimer = null;
    lobbyGen++;
    closeSession();
    if (roomId && (status === "listening" || status === "joining"))
      void voiceLeave(roomId).catch(() => {});
    unsubscribeRoom?.();
    unsubscribeRoom = null;
    unsubscribeLobby?.();
    unsubscribeLobby = null;
    nicknames.clear();
    leftForHide = false;
    ackReposted = false;
    resets = [];
    refs = 0;
    set({
      ...IDLE_SESSION,
      status: "idle",
      roomId: null,
      myPlayerId: null,
      screenRoomId: null,
      lobby: null,
      hostId: null,
      members: {},
      hostMute: NO_HOST_MUTE,
      tableOff: false,
      micBlocked: false,
      calloutDismissed: false,
      sheetOpen: false,
      mutedIds: {},
    });
  }

  return {
    status: "idle",
    roomId: null,
    myPlayerId: null,
    screenRoomId: null,
    lobby: null,
    hostId: null,
    members: {},
    hostMute: NO_HOST_MUTE,
    tableOff: false,
    ...IDLE_SESSION,
    micBlocked: false,
    calloutDismissed: false,
    calloutRetired: false,
    sheetOpen: false,
    mutedIds: {},

    retain: (lobbyId, myPlayerId) => {
      // Voice depends on the privacy policy; a build that hides the policy
      // (a draft, in production) has no voice either.
      if (!lobbyId || !myPlayerId || !privacyLinkVisible()) return;
      const s = get();
      if (s.roomId === lobbyId && s.myPlayerId === myPlayerId) {
        refs++;
        if (releaseTimer !== null) clearTimeout(releaseTimer);
        releaseTimer = null;
        return;
      }
      if (s.roomId) teardown();
      refs = 1;
      const gen = lobbyGen;
      muteList = loadMutes();
      set({
        roomId: lobbyId,
        myPlayerId,
        calloutRetired: mutedCalloutRetired(),
      });
      installPageListeners();
      if (isAgeGated()) {
        set({ status: "under13" });
        return;
      }
      unsubscribeRoom = subscribeToVoiceRoom(lobbyId, (room) => {
        if (gen !== lobbyGen) return;
        set({
          members: room.members,
          hostMute: room.hostMute,
          tableOff: room.tableOff,
        });
        recomputeMuted();
        applyTableOff(room.tableOff);
        followHostMute();
        reconcile();
      });
      // The lobby's own document, which every room is derived from. A listener
      // of the store's own rather than the screens': in a party the play
      // screen is subscribed to its table, not to the lobby.
      unsubscribeLobby = subscribeToGame(lobbyId, (state) => {
        if (gen !== lobbyGen) return;
        const prevLobby = get().lobby;
        const nextLobby: RoomLobby = {
          id: state.id,
          phase: state.phase,
          party: state.party ?? null,
          // Whereabouts move a player between rooms too (rooms.ts), so they
          // are part of what a room is derived from.
          players: (state.players ?? []).map((p) => ({
            id: p.id,
            viewing: p.viewing ?? null,
            team: p.team ?? null,
          })),
          observers: (state.observers ?? []).map((p) => ({
            id: p.id,
            viewing: p.viewing ?? null,
          })),
          // A Teams round is heard by team (rooms.ts), so mode and teams are
          // part of what a room is derived from.
          mode: state.mode ?? null,
          teams: state.teams ?? null,
        };
        set({ lobby: nextLobby, hostId: hostOf(state) });
        announceDerivedChange(prevLobby, nextLobby);
        arrive();
        reconcile();
      });
      if (!readyBand()) set({ status: "needs-consent" });
      else if (shouldAutoJoin()) void get().join();
    },

    setScreenRoom: (lobbyId, screenRoomId) => {
      const prev = get().screenRoomId;
      if (get().roomId !== lobbyId || prev === screenRoomId) return;
      set({ screenRoomId });
      reconcile();
      // The first room is where this match's voice started, not a move.
      if (prev === null || get().status !== "listening") {
        pendingArrival = null;
        return;
      }
      pendingArrival = screenRoomId;
      arrive();
    },

    release: (lobbyId) => {
      if (get().roomId !== lobbyId || refs === 0) return;
      refs--;
      if (refs > 0) return;
      if (releaseTimer !== null) clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        releaseTimer = null;
        if (refs === 0) teardown();
      }, RELEASE_DELAY_MS);
    },

    setRoster: (players) => {
      let changed = false;
      for (const p of players) {
        if (nicknames.get(p.id) !== p.nickname) {
          nicknames.set(p.id, p.nickname);
          changed = true;
        }
      }
      if (!changed) return;
      recomputeMuted();
      reconcile();
    },

    join: async (interactive = false) => {
      const { roomId, status } = get();
      if (
        !roomId ||
        status === "joining" ||
        status === "listening" ||
        status === "under13"
      )
        return;
      if (isAgeGated()) {
        set({ status: "under13" });
        return;
      }
      const band = readyBand();
      if (!band) {
        set({ status: "needs-consent", sheetOpen: interactive });
        return;
      }
      // The server would answer table-off; the roster already has.
      if (get().tableOff) {
        set({ status: "table-off" });
        return;
      }
      // Inside the tap when there is one: the context this creates is the one
      // iOS will let play.
      ensureVoiceContext();
      const gen = sessionGen;
      set({ status: "joining" });
      try {
        const res = await voiceJoin(roomId);
        if (gen !== sessionGen) {
          // Left (or moved on) while the join was in flight: the room must not
          // keep a member this client has already walked away from.
          void voiceLeave(roomId).catch(() => {});
          return;
        }
        client = new VoiceClient({
          roomId,
          iceServers: res.iceServers,
          plan: () => {
            const { members, myPlayerId, lobby, screenRoomId } = get();
            const room = { members };
            return {
              room,
              desired: roomPulls(
                room,
                myPlayerId ?? "",
                isMutedFor(roomId),
                lobby,
                roomId,
                screenRoomId ?? roomId,
              ),
            };
          },
          onChange: () => set({ published: client?.published ?? false }),
          onBroken: () => reconnect(),
          onEmptied: () => renewSession(),
        });
        // First in the client's queue: every pull and publish waits behind it.
        client.connect();
        set({
          ...IDLE_SESSION,
          status: "listening",
          micBlocked: false,
          needsAudioUnlock: voiceAudioNeedsUnlock(),
        });
        // Turned off while the join was in flight: that roster arrived before
        // there was a session to hang up.
        if (get().tableOff) {
          applyTableOff(true);
          return;
        }
        get().refreshOutput();
        startSpeaking();
        reconcile();
      } catch (err) {
        if (gen !== sessionGen) return;
        const code = voiceErrorCode(err);
        if (code === "voice-off" || code === "not-in-game") {
          set({ status: "unavailable" });
        } else if (code === "table-off") {
          set({ status: "table-off" });
        } else if (code === "policy" || code === "age") {
          // The server holds no acknowledgement for this uid although this
          // device does: a sign-in that landed on a different uid. Re-post
          // the one held here once before asking the player again.
          if (!ackReposted) {
            ackReposted = true;
            try {
              await acknowledgePrivacy(PRIVACY_MATERIAL_VERSION, band);
              if (gen !== sessionGen) return;
              set({ status: "idle" });
              return get().join(interactive);
            } catch {
              /* fall through to asking */
            }
          }
          set({ status: "needs-consent", sheetOpen: interactive });
        } else {
          if (import.meta.env.DEV) console.warn("[voice] join failed:", err);
          set({ status: "error" });
        }
      }
    },

    leave: () => {
      const { roomId, status } = get();
      closeSession();
      if (roomId && (status === "listening" || status === "joining"))
        void voiceLeave(roomId).catch(() => {});
      set({
        ...IDLE_SESSION,
        status:
          status === "listening" || status === "joining" ? "idle" : status,
      });
    },

    toggleMic: () => {
      const s = get();
      const c = client;
      if (s.status !== "listening" || !c || s.micBusy) return;
      // The button is disabled under a host mute; this is the backstop for a
      // tap that raced the roster. The server would refuse the publish anyway.
      if (!s.micOn && s.myPlayerId && s.members[s.myPlayerId]?.hostMuted)
        return;
      if (s.micOn) {
        // Unpublishes and releases the mic (voiceClient's mute says why), so
        // the next unmute below is a fresh publish.
        c.mute();
        set({ micOn: false });
        c.sendMicState(true).catch(() => {});
        return;
      }
      if (c.published) {
        // Published with the mic off: only the moment a host mute lands before
        // its force-close, since muting unpublishes. Turning it on is instant.
        c.setMicEnabled(true);
        set({ micOn: true, micBlocked: false });
        if (s.roomId) recordUnmutedMatch(s.roomId);
        c.sendMicState(false).catch(() => {});
        return;
      }
      // The first unmute this match publishes, and that is when the browser
      // asks for the mic: the moment the player has just asked to talk, never
      // on arrival. publish() calls getUserMedia before its first await, so
      // the prompt is raised inside this tap.
      const gen = sessionGen;
      c.setMicEnabled(true);
      set({ micBusy: true, micBlocked: false });
      c.publish().then(
        () => {
          if (gen !== sessionGen) return;
          if (micOffAfterPublish) {
            // Moved to watching another table while the publish was in flight.
            // The roster still says selfMuted from the join: nothing to send.
            micOffAfterPublish = false;
            c.mute();
            set({ micOn: false, micBusy: false, published: c.published });
            return;
          }
          set({ micOn: true, micBusy: false, published: true });
          if (s.roomId) recordUnmutedMatch(s.roomId);
          return c.sendMicState(false).catch(() => {});
        },
        (err: unknown) => {
          if (gen !== sessionGen) return;
          c.setMicEnabled(false);
          set({ micOn: false, micBusy: false, micBlocked: micRefused(err) });
        },
      );
    },

    mutePlayer: (playerId, nickname) => {
      const { roomId } = get();
      if (!roomId) return;
      mute(playerId, nickname, roomId);
      muteList = loadMutes();
      recomputeMuted();
      // Silent on the tap; the close that stops the track follows in the queue.
      for (const id of Object.keys(get().mutedIds)) silenceRemote(id);
      reconcile();
    },

    unmutePlayer: (playerId, nickname) => {
      const { roomId } = get();
      if (!roomId) return;
      unmute(playerId, nickname, roomId);
      muteList = loadMutes();
      recomputeMuted();
      reconcile();
    },

    unlockAudio: () => {
      unlockVoiceAudio();
      set({
        needsAudioUnlock:
          get().status === "listening" && voiceAudioNeedsUnlock(),
      });
    },

    /**
     * The speaker button silences everything a player hears, voices included:
     * a phone on a bus that suddenly plays strangers gets the speaker reached
     * for, and a speaker that left the voices on would be the one control on
     * the table that did not do what its icon says. Settings' Voice level is
     * the way to keep voices with the music off.
     */
    refreshOutput: () => {
      const silenced = !sound.isMusicOn() && !sound.isSfxOn();
      setVoiceOutputLevel(silenced ? 0 : getVoiceLevel());
    },

    dismissCallout: () => set({ calloutDismissed: true }),
    openSheet: () => set({ sheetOpen: true }),
    closeSheet: () => set({ sheetOpen: false }),

    consented: () => {
      ackReposted = false;
      set({ sheetOpen: false, status: "idle" });
      void get().join(true);
    },

    markUnder13: () => {
      unsubscribeRoom?.();
      unsubscribeRoom = null;
      unsubscribeLobby?.();
      unsubscribeLobby = null;
      set({ status: "under13", members: {} });
    },

    hostMutePlayer: (playerId, muted) => {
      const { roomId } = get();
      return roomId
        ? hostCall(() => voiceHostMute(roomId, { playerId, muted }))
        : Promise.resolve(false);
    },

    hostMuteAll: (all) => {
      const { roomId } = get();
      return roomId
        ? hostCall(() => voiceHostMute(roomId, { all }))
        : Promise.resolve(false);
    },

    setTableVoice: (off) => {
      const { roomId } = get();
      return roomId
        ? hostCall(() => voiceTable(roomId, off))
        : Promise.resolve(false);
    },
  };
});

/**
 * Whether any voice control is drawn: a build that shows the privacy policy, a
 * match that has retained voice, and not a device that answered under 13.
 */
export function useVoiceUiShown(): boolean {
  const roomId = useVoiceStore((s) => s.roomId);
  const status = useVoiceStore((s) => s.status);
  return privacyLinkVisible() && !!roomId && status !== "under13";
}

/**
 * Keeps voice up while an online screen of this match is mounted. Every online
 * screen calls it, so the room survives moving between them and is left a few
 * seconds after the last one goes.
 *
 * `roster` feeds the nickname half of personal mutes - a muted guest who
 * rejoins under a new uid is caught by the name they had in this match.
 *
 * `screenRoomId` is the room this screen shows: the table's game id on the
 * play screen (a party instance's, or the lobby's own at one table), the lobby
 * id everywhere else. The ears follow the eyes, so a player still watching
 * their own table's podium is not switched to the room the server would
 * already derive for them.
 */
export function useVoiceMatch(
  lobbyId: string,
  myPlayerId: string,
  roster: { id: string; nickname: string }[] | undefined,
  screenRoomId: string,
) {
  useEffect(() => {
    if (!lobbyId || !myPlayerId) return;
    useVoiceStore.getState().retain(lobbyId, myPlayerId);
    return () => useVoiceStore.getState().release(lobbyId);
  }, [lobbyId, myPlayerId]);

  // After the retain above, which is what makes `lobbyId` the store's room.
  useEffect(() => {
    if (!lobbyId || !myPlayerId || !screenRoomId) return;
    useVoiceStore.getState().setScreenRoom(lobbyId, screenRoomId);
  }, [lobbyId, myPlayerId, screenRoomId]);

  // Keyed on the roster's content, not the array, which is a new object on
  // every snapshot of a document rewritten by every move.
  const rosterKey = useMemo(
    () =>
      (roster ?? []).map((p) => `${p.id}\u0000${p.nickname}`).join("\u0001"),
    [roster],
  );
  const latest = useRef(roster);
  latest.current = roster;
  useEffect(() => {
    const players = latest.current;
    if (players && players.length) useVoiceStore.getState().setRoster(players);
  }, [rosterKey]);
}
